import { Button, Checkbox, Input, Modal, Upload } from 'antd';
import * as React from 'react';
import { useState } from 'react';
import { CloseOutlined, DownOutlined, SearchOutlined, UploadOutlined, UpOutlined } from '@ant-design/icons';
import './style.css';

const extractDomainName = (value: string): string => {
    let host = value.trim()
    if (!host) return host
    host = host.replace(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//, '')
    host = host.split('/')[0].split('?')[0].split('#')[0]
    host = host.replace(/^www\./i, '')
    host = host.replace(/:\d+$/, '')
    return host
}

const DOMAIN_REGEX = /^([A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/

const isValidDomain = (value: string): boolean => DOMAIN_REGEX.test(value)

export interface DomainListCardProps {
    title: string
    subtitle: string
    items: string[]
    acceptAll: boolean
    blackList: boolean
    collapsed: boolean
    onToggleCollapse: () => void
    onAcceptAllChange: (val: boolean) => void
    onBlackListChange: (val: boolean) => void
    onAdd: (value: string) => void
    onUpload: (file: File) => void
    onRemoveItem: (index: number) => void
    onClearAll: () => void
    initialFileLabel?: string | null
    showBlockListToggle?: boolean
}

interface RemovableItemListProps {
    entries: { item: string; idx: number }[]
    onRemove: (index: number) => void
    disabled?: boolean
    emptyText?: string
}

const BATCH_SIZE = 100
const CONTAINER_HEIGHT = 400

const RemovableItemList: React.FC<RemovableItemListProps> = ({
    entries, onRemove, disabled = false, emptyText = 'No items yet',
}) => {
    const [visibleCount, setVisibleCount] = useState(BATCH_SIZE)
    const scrollRef = React.useRef<HTMLDivElement>(null)

    React.useEffect(() => { setVisibleCount(BATCH_SIZE) }, [entries.length])

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
            setVisibleCount(prev => Math.min(prev + BATCH_SIZE, entries.length))
        }
    }

    if (entries.length === 0) {
        return (
            <div style={{ border: '1px solid #e8e8e8', borderRadius: 6, padding: '10px 12px', minHeight: 48 }}>
                <span style={{ color: '#bbb', fontSize: 13 }}>{emptyText}</span>
            </div>
        )
    }

    const visibleItems = entries.slice(0, visibleCount)

    return (
        <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="removable-item-list"
            style={{
                border: '1px solid #e8e8e8', borderRadius: 6,
                padding: '10px 12px',
                maxHeight: CONTAINER_HEIGHT,
                overflowY: 'auto',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px',
                alignContent: 'start',
            }}
        >
            {visibleItems.map(({ item, idx }) => (
                <div key={item} className="ril-chip" title={item}>
                    <button
                        type="button"
                        className="ril-remove"
                        disabled={disabled}
                        aria-label={`Remove ${item}`}
                        onClick={() => { if (!disabled) onRemove(idx) }}
                    >
                        <CloseOutlined />
                    </button>
                    <span className="ril-label">{item}</span>
                </div>
            ))}
            {visibleCount < entries.length && (
                <div style={{ flexBasis: '100%', width: '100%', textAlign: 'center', color: '#888', fontSize: 12, padding: '4px 0' }}>
                    Showing {visibleCount} of {entries.length} — scroll to load more
                </div>
            )}
        </div>
    )
}

const DomainListCard: React.FC<DomainListCardProps> = ({
    title, subtitle, items, acceptAll, blackList,
    collapsed, onToggleCollapse,
    onAcceptAllChange, onBlackListChange,
    onAdd, onUpload, onRemoveItem, onClearAll,
    initialFileLabel,
    showBlockListToggle = true,
}) => {
    const [inputValue, setInputValue] = useState('')
    const [validationMsg, setValidationMsg] = useState('')
    const [search, setSearch] = useState('')
    const [fileLabel, setFileLabel] = useState<string | null>(initialFileLabel ?? null)
    const [showBlockListConfirm, setShowBlockListConfirm] = useState(false)
    const [showProcessingModal, setShowProcessingModal] = useState(false)
    const [showReviewModal, setShowReviewModal] = useState(false)
    const [parsedItems, setParsedItems] = useState<string[]>([])
    const [csvFileName, setCsvFileName] = useState('')
    const [progress, setProgress] = useState(0)
    const [invalidDroppedCount, setInvalidDroppedCount] = useState(0)

    React.useEffect(() => {
        if (initialFileLabel !== undefined) setFileLabel(initialFileLabel ?? null)
    }, [initialFileLabel])

    React.useEffect(() => {
        if (items.length === 0) {
            setFileLabel(null)
            setSearch('')
        }
    }, [items.length])

    const filtered = items
        .map((item, idx) => ({ item, idx }))
        .filter(({ item }) => item.toLowerCase().includes(search.toLowerCase()))

    const handleAdd = () => {
        const trimmed = inputValue.trim()
        if (!trimmed) return
        const value = isDomainsCard ? extractDomainName(trimmed) : trimmed
        if (!value) return
        if (isDomainsCard && !isValidDomain(value)) {
            setValidationMsg('Please enter a valid domain name.')
            return
        }
        if (items.some(d => d.toLowerCase() === value.toLowerCase())) {
            setValidationMsg(`"${value}" has already been added.`)
            return
        }
        setValidationMsg('')
        onAdd(value)
        setInputValue('')
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') { e.preventDefault(); handleAdd() }
    }

    const handleFileUpload = (file: File) => {
        setCsvFileName(file.name)
        setProgress(0)
        setShowProcessingModal(true)
        const reader = new FileReader()
        reader.onloadend = () => {
            let prog = 0
            const interval = setInterval(() => {
                prog += 20
                setProgress(prog)
                if (prog >= 100) {
                    clearInterval(interval)
                    const rawLines = (reader.result as string)
                        .split('\n').map(l => l.trim()).filter(Boolean)
                    let lines = rawLines
                    let dropped = 0
                    if (isDomainsCard) {
                        lines = []
                        rawLines.forEach(l => {
                            const domain = extractDomainName(l)
                            if (domain && isValidDomain(domain)) lines.push(domain)
                            else dropped++
                        })
                    }
                    setInvalidDroppedCount(dropped)
                    setParsedItems(lines)
                    setShowProcessingModal(false)
                    setShowReviewModal(true)
                }
            }, 80)
        }
        reader.readAsText(file)
        return false
    }

    const handleConfirmImport = () => {
        const seen = new Set(items.map(d => d.toLowerCase()))
        const unique: string[] = []
        parsedItems.forEach(d => {
            const key = d.toLowerCase()
            if (seen.has(key)) return
            seen.add(key)
            unique.push(d)
        })
        unique.forEach(item => onAdd(item))
        setFileLabel(isDomainsCard ? 'domains.csv' : 'mobileAppIds.csv')
        setShowReviewModal(false)
        setParsedItems([])
    }

    const handleRemoveParsedItem = (idx: number) => {
        setParsedItems(prev => prev.filter((_, i) => i !== idx))
    }

    const handleToggleBlackList = () => {
        if (!blackList) {
            setShowBlockListConfirm(true)
        } else {
            onBlackListChange(false)
        }
    }

    const handleDownload = () => {
        if (items.length === 0) return
        const csvContent = items.join('\n')
        const blob = new Blob([csvContent], { type: 'text/csv' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = isDomainsCard ? 'domains.csv' : 'mobileAppIds.csv'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }

    const isDomainsCard = title === 'Web Domains'

    return (
        <div style={{ border: '1px solid #e8e8e8', borderRadius: 8, marginBottom: 16, background: '#fff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px' }}>
                <div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: 'rgba(0,0,0,0.85)' }}>{title}</div>
                    <div style={{ color: '#595959', fontSize: 13 }}>{subtitle}</div>
                </div>
                <button type="button" onClick={onToggleCollapse} style={{
                    background: 'none', border: '1px solid #d9d9d9', borderRadius: '50%',
                    width: 32, height: 32, cursor: 'pointer', fontSize: 14,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'rgba(0,0,0,0.65)',
                }}>
                    {collapsed ? <DownOutlined /> : <UpOutlined />}
                </button>
            </div>

            {!collapsed && (
                <div style={{ padding: '0 20px 20px' }}>
                    <div style={{ fontWeight: 600, marginBottom: 8, color: 'rgba(0,0,0,0.85)' }}>
                        Add {isDomainsCard ? 'Domains' : 'Mobile App IDs'}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 4, alignItems: 'center' }}>
                        <Input
                            value={inputValue}
                            onChange={e => { setInputValue(e.target.value); setValidationMsg('') }}
                            onKeyDown={handleKeyDown}
                            placeholder={isDomainsCard ? 'Enter domain' : 'Enter mobile app ID'}
                            style={{ flex: 1, minWidth: 0 }}
                            disabled={acceptAll}
                        />
                        <Button type="primary" onClick={handleAdd} disabled={acceptAll} style={{ flexShrink: 0, minWidth: 72 }}>Add</Button>
                        <Upload accept=".csv,.txt" beforeUpload={handleFileUpload} showUploadList={false}>
                            <Button icon={<UploadOutlined />} disabled={acceptAll} style={{ flexShrink: 0 }}>Upload</Button>
                        </Upload>
                    </div>
                    {fileLabel && (
                        <div style={{ fontSize: 12, color: '#888', textAlign: 'right', marginBottom: 4 }}>
                            <span onClick={handleDownload} title="Download uploaded CSV" style={{ cursor: 'pointer' }}>
                                {fileLabel}
                            </span>{' '}
                            <span style={{ cursor: 'pointer', color: '#ff4d4f' }} onClick={onClearAll}>×</span>
                        </div>
                    )}
                    <div style={{ color: '#ff4d4f', fontSize: 12, lineHeight: '18px', minHeight: 18, marginBottom: 4 }}>{validationMsg}</div>

                    <div style={{ fontWeight: 600, margin: '12px 0 8px', color: 'rgba(0,0,0,0.85)' }}>
                        Manage {isDomainsCard ? 'Domain' : 'Mobile App ID'} Listing
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        {showBlockListToggle ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span style={{ fontSize: 14, color: 'rgba(0,0,0,0.85)' }}>Allow List</span>
                                <div onClick={handleToggleBlackList} style={{
                                    width: 44, height: 24, borderRadius: 12,
                                    background: blackList ? '#ff4d4f' : '#1677ff',
                                    cursor: 'pointer', position: 'relative', transition: 'background 0.2s',
                                }}>
                                    <div style={{
                                        width: 18, height: 18, borderRadius: '50%', background: '#fff',
                                        position: 'absolute', top: 3,
                                        left: blackList ? 23 : 3, transition: 'left 0.2s',
                                    }} />
                                </div>
                                <span style={{ fontSize: 14, color: 'rgba(0,0,0,0.85)' }}>Block List</span>
                            </div>
                        ) : <div />}
                        <Checkbox checked={acceptAll} onChange={e => onAcceptAllChange(e.target.checked)}>All</Checkbox>
                    </div>

                    {acceptAll ? (
                        <div style={{ background: '#fafafa', border: '1px solid #e8e8e8', borderRadius: 6, padding: '32px 16px', textAlign: 'center' }}>
                            <div style={{ fontWeight: 500, marginBottom: 4 }}>All Option is Enabled</div>
                            <div style={{ color: '#888', fontSize: 13 }}>
                                Uncheck 'All' to access {isDomainsCard ? 'domain' : 'mobile app ID'} List.
                            </div>
                        </div>
                    ) : (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <span style={{ fontWeight: 500, color: 'rgba(0,0,0,0.85)' }}>
                                    {isDomainsCard ? 'Domains' : 'Mobile App IDs'} List{' '}
                                    <span style={{ background: '#f0f0f0', borderRadius: 10, padding: '0 8px', fontSize: 12 }}>{items.length}</span>
                                </span>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <Button size="small" onClick={onClearAll}>Clear All</Button>
                                </div>
                            </div>
                            <Input
                                prefix={<SearchOutlined style={{ color: '#bbb' }} />}
                                placeholder={`Search ${isDomainsCard ? 'Domains' : 'Mobile App IDs'}`}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                style={{ marginBottom: 8 }}
                            />
                            <RemovableItemList
                                entries={filtered}
                                onRemove={onRemoveItem}
                            />
                        </>
                    )}
                </div>
            )}

            <Modal open={showProcessingModal} footer={null} closable={false} centered width={360}>
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <div style={{ fontWeight: 700, fontSize: 16, color: 'rgba(0,0,0,0.85)', marginBottom: 12 }}>Processing {csvFileName}</div>
                    <div style={{ color: '#595959', fontSize: 14, marginBottom: 16 }}>Parsing {isDomainsCard ? 'Domains' : 'Mobile App IDs'}</div>
                    <div style={{ height: 8, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: 4, background: '#1677ff', width: `${progress}%`, transition: 'width 0.08s linear' }} />
                    </div>
                </div>
            </Modal>

            <Modal open={showReviewModal} title="Review CSV import" footer={null} onCancel={() => { setShowReviewModal(false); setParsedItems([]) }} centered width={480}>
                <p style={{ color: '#595959', fontSize: 14, marginBottom: 12 }}>
                    {parsedItems.length} {isDomainsCard ? 'domains' : 'mobile app IDs'} found in {csvFileName}. These will be added to your allow list.
                </p>
                {isDomainsCard && invalidDroppedCount > 0 && (
                    <p style={{ color: '#ff4d4f', fontSize: 13, marginBottom: 12 }}>
                        {invalidDroppedCount} invalid domain{invalidDroppedCount > 1 ? 's were' : ' was'} dropped.
                    </p>
                )}
                <div style={{ border: '1px solid #e8e8e8', borderRadius: 6, padding: '10px 12px', minHeight: 60, maxHeight: 400, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20, alignContent: 'flex-start' }}>
                    {parsedItems.map((item, idx) => (
                        <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#f5f5f5', border: '1px solid #d9d9d9', borderRadius: 4, padding: '2px 8px', fontSize: 13, color: 'rgba(0,0,0,0.85)' }}>
                            {item}
                            <span style={{ cursor: 'pointer', color: '#999', fontWeight: 700, marginLeft: 2 }} onClick={() => handleRemoveParsedItem(idx)}>×</span>
                        </span>
                    ))}
                    {parsedItems.length === 0 && <span style={{ color: '#bbb', fontSize: 13 }}>No items</span>}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                    <Button onClick={() => { setShowReviewModal(false); setParsedItems([]) }}>Cancel</Button>
                    <Button type="primary" disabled={parsedItems.length === 0} onClick={handleConfirmImport}>
                        Add {parsedItems.length} {isDomainsCard ? 'Domains' : 'Mobile App IDs'}
                    </Button>
                </div>
            </Modal>

            <Modal open={showBlockListConfirm} title="Switch to block list?" footer={null} onCancel={() => setShowBlockListConfirm(false)} width={420}>
                <p style={{ color: '#595959', fontSize: 14, marginBottom: 16 }}>
                    You're about to switch from allow list to block list. Only one list type can be active at a time.
                </p>
                <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, padding: '10px 14px', marginBottom: 20, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <span>⚠</span>
                    <span>All {items.length} {isDomainsCard ? 'domains' : 'mobile app IDs'} in your current allow list will be permanently moved.</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                    <Button onClick={() => setShowBlockListConfirm(false)}>Keep Allow List</Button>
                    <Button type="primary" danger onClick={() => { onBlackListChange(true); setShowBlockListConfirm(false) }}>Switch To Block List</Button>
                </div>
            </Modal>
        </div>
    )
}

export default DomainListCard;